import React from "react";
import { ChooseContactRadio } from "./ChooseContactRadio";
import { useCustomQuery, usePrevious } from "utilities/hookutils";
import { Loading } from "components/panels";
import ContactModalPicker from "components/Picker/ContactPicker/ContactModalPicker";

interface Props {
  companyId: number;
  selectedContact: server.dto.ContactSmallV2
  onContactChanged: (contact: server.dto.ContactSmallV2) => void
}

const useContactsByCompanyQuery = (companyId: number) => {
  return useCustomQuery({
    queryKey: ["contactLinkedToCompany", companyId],
    queryFn: () => window.CoreApi.CompanyToContact.ListContactByCompany({
      companyId: companyId,
      page: 1,
      perPage: 1000,
      isActive: true
    }).then(r => r.data.data),
    initialData: [],
  });
}

const ChooseContact = (props: Props) => {
  const contactsApi = useContactsByCompanyQuery(props.companyId);

  // Sometimes a contact is selected that's not linked to the companyId
  // We must display this contact in the list too
  // It remains there until the api returns new data & the contact is no longer in that list
  const [extraContact, setExtraContact] = React.useState<server.dto.ContactSmallV2>();

  const previousData = usePrevious(contactsApi.data);
  React.useEffect(() => {
    if (previousData === contactsApi.data) return;

    if (props.selectedContact && contactsApi.data.find(c => c.contact.contactId !== props.selectedContact?.contactId)) {
      setExtraContact(props.selectedContact);
    } else {
      setExtraContact(undefined);
    }
  }, [previousData, props.selectedContact, contactsApi.data]);

  const onContactSelected = (contact: server.dto.ContactSmallV2) => {
    props.onContactChanged(contact);
  };

  const onContactLinked = async (contact: server.dto.ContactSmallV2) => {
    const instruction: server.dto.SetCompanyToContactLinkInstruction = {
      companyId: props.companyId,
      contactId: contact.contactId,
      isActive: true,
      defaultSelect: false
    };
    const res = await window.CoreApi.CompanyToContact.SetLink(instruction);
    if (res.data.hasErrors) {
      window.displayGenericModal("Error", res.data.errors[0].message, "tiny", "red");
      return;
    }

    contactsApi.refetch();

    props.onContactChanged(contact);
  };

  const canEditContacts = site.permissions.addressBook.canCreate;

  const contactOptions = [...contactsApi.data.map(c => c.contact as server.dto.ContactSmallV2)];
  if (extraContact && !contactOptions.find(c => c.contactId === extraContact.contactId)) {
    contactOptions.push(extraContact);
  }

  return <div className="flex-column gap">
    <div>
      {contactsApi.isFetching && <Loading />}
      {!contactsApi.isFetching && <ChooseContactRadio
        contactOptions={contactOptions}
        selectedContact={props.selectedContact}
        onContactSelected={onContactSelected}
        onContactEdited={contactsApi.refetch}
      />}
    </div>
    {canEditContacts && <div>
      <ContactModalPicker
        afterCreate="closeSidebar"
        buttonLabel={RESX.Contact.LinkExistingContact}
        canCreateNew={canEditContacts}
        nonInPageEditing={{
          onChange: onContactLinked,
          value: null,
          required: false,
          validatorKey: "",
        }}
      />
    </div>}
  </div>;
};

export default ChooseContact;